import { StateCreator } from 'zustand';
import type {
  IntakeDocumentResponse,
  AgencyInsuranceResponse,
  UserResponse,
} from '../../services/api/types.gen';

export interface RecommendationItem {
  severity: 'normal' | 'moderate' | 'attention' | 'critical';
  recommendation: string;
}

export interface IntakeRecord {
  id: string;
  status:
    | 'received'
    | 'pending_review'
    | 'under_review'
    | 'pending_clarification'
    | 'pending_eligibility'
    | 'pending_prior_auth'
    | 'accepted'
    | 'ready_to_call'
    | 'scheduled'
    | 'pushed_to_kinnser'
    | 'pushed_to_roger'
    | 'rejected'
    | 'duplicate'
    | 'expired';

  // Document information
  fax_pdf_path: string;
  file_size: number;
  ocr_text: string;
  is_processed: boolean;
  processing_errors?: string;
  workflow_id?: string;
  processing_status?: string;

  // Patient information
  patient_first_name: string;
  patient_middle_name?: string | null;
  patient_last_name: string;
  patient_suffix?: string | null;
  patient_dob?: Date | string;
  patient_gender?: string; // M/F/U
  patient_ssn?: string | null;
  patient_phone?: string | null;
  patient_email?: string | null;
  patient_street_address?: string | null;
  patient_city?: string | null;
  patient_state?: string | null;
  patient_zip_code?: string | null;
  patient_county?: string | null;
  patient_primary_language?: string | null;
  medicaid_id?: string | null;

  // Clinic information
  agency_clinic_id?: string;

  // Insurance information
  primary_insurance: string;
  primary_member_id: string;
  primary_payor_id?: string;
  agency_insurance_id?: string;
  agency_insurance?: AgencyInsuranceResponse;
  secondary_insurance: string;
  secondary_member_id: string;
  secondary_payor_id?: string;

  // Referring provider information
  referring_physician: string;
  referring_phone: string;
  referring_fax: string;

  // Referral source information
  discharge_facility_name: string;
  discharge_facility_type: string;
  referral_source: string;

  // Clinical information
  primary_diagnosis: string;
  diagnosis_codes: string[];
  services_requested: string[];
  frequency_duration: string;
  physician_orders_included: boolean;

  // eFax specific fields
  fax_number_from: string;
  fax_number_to: string;
  efax_message_id: string;
  total_pages: number;

  // Patient relationship
  patient_id?: string;

  // Assignment information
  assigned_to?: string;
  assigned_date?: Date | string;
  assigned_to_user?: UserResponse | null;

  // Business rules and flags
  requires_prior_auth: boolean;
  insurance_verified: boolean;
  homebound_status_verified: boolean;
  in_service_area: boolean;

  // SLA and escalation
  sla_deadline?: Date | string;
  escalated: boolean;
  escalated_date?: Date | string;
  escalated_reason: string;

  // Duplicate tracking
  is_duplicate: boolean;
  original_intake_id?: string;

  // Review and decision fields
  priority: string;
  reviewed_by?: string;
  reviewed_date?: Date | string;
  decision_date?: Date | string;
  decision_notes?: string;
  rejection_reason?: string;
  rejection_details?: string;
  created_admission_id?: string;

  // Authorization tracking
  authorization_status?: string;
  authorization_number?: string;
  authorization_request_date?: Date | string;
  authorization_last_updated?: Date | string;
  authorization_notes?: string;
  authorized_disciplines_frequency?: string;
  authorization_document_id?: string;

  // AI Analysis fields
  risk_score?: number;
  ai_summary?: string;
  ai_recommendations?: RecommendationItem[];
  last_ai_analysis_date?: Date | string;
  last_ai_analysis_status?: string;

  // Comments
  comments?: string;

  // Timestamps
  created_at: Date | string;
  updated_at: Date | string;

  // Kinnser integration fields
  kinnser_clinic_id?: string | null;
  kinnser_physician_id?: string | null;
  kinnser_branch_id?: string | null;
  kinnser_insurance_provider_id?: string | null;
  kinnser_admission_type?: string | null;
  kinnser_hcpcs_code_key?: string | null;
  kinnser_mrn?: string | null;
  kinnser_hh_cahps_opt_out?: boolean | null;
  kinnser_language_key?: string | null;
  kinnser_clinician_id?: string | null;
  kinnser_clinical_care_manager_id?: string | null;
  kinnser_case_manager_id?: string | null;
  kinnser_oasis_approver_id?: string | null;
  kinnser_pt_approver_id?: string | null;
  kinnser_ot_approver_id?: string | null;
  kinnser_st_approver_id?: string | null;
  kinnser_soc_date?: string | null;
  kinnser_create_episode?: boolean | null;
  roger_create_episode?: boolean | null;
  roger_type_of_soc?: string | null;
  push_to_kinnser?: boolean | null;
  push_to_roger?: boolean | null;

  // Documents
  documents?: Array<IntakeDocumentResponse>;
  patient_notes?: string;

  // Per-tab completion statuses
  tab_statuses?: {
    extraction?: 'incomplete' | 'complete' | 'complete_with_errors' | 'skipped';
    eligibility?:
      | 'incomplete'
      | 'complete'
      | 'complete_with_errors'
      | 'skipped';
    prior_auth?: 'incomplete' | 'complete' | 'complete_with_errors' | 'skipped';
    ready_to_call?:
      | 'incomplete'
      | 'complete'
      | 'complete_with_errors'
      | 'skipped';
    scheduling?: 'incomplete' | 'complete' | 'complete_with_errors' | 'skipped';
    push_to_emr?:
      | 'incomplete'
      | 'complete'
      | 'complete_with_errors'
      | 'skipped';
  };

  // Legacy fields for backward compatibility
  documentUrl?: string;
  error?: string;
}

// Filter state for IntakeManager persistence
export interface IntakeManagerFilters {
  statusFilters: string[];
}

export interface IntakeSlice {
  // Upload state
  uploadStatus: 'idle' | 'uploading' | 'success' | 'error';
  uploadProgress: number;
  uploadError: string | null;
  isExtracting: boolean;

  // Intake data
  intakes: IntakeRecord[];
  selectedIntake: IntakeRecord | null;
  intakesLoading: boolean;
  intakesError: string | null;

  // Persisted filter state for IntakeManager
  intakeManagerFilters: IntakeManagerFilters;

  // Actions
  setUploadStatus: (status: 'idle' | 'uploading' | 'success' | 'error') => void;
  setUploadProgress: (progress: number) => void;
  setUploadError: (error: string | null) => void;
  setIsExtracting: (isExtracting: boolean) => void;
  setIntakes: (intakes: IntakeRecord[]) => void;
  addIntake: (intake: IntakeRecord) => void;
  updateIntake: (id: string, intake: IntakeRecord) => void;
  setSelectedIntake: (intake: IntakeRecord | null) => void;
  setIntakesLoading: (loading: boolean) => void;
  setIntakesError: (error: string | null) => void;
  resetUploadState: () => void;
  setIntakeManagerFilters: (filters: IntakeManagerFilters) => void;
}

export const intakeSlice: StateCreator<IntakeSlice> = set => ({
  // Initial state
  uploadStatus: 'idle',
  uploadProgress: 0,
  uploadError: null,
  isExtracting: false,
  intakes: [],
  selectedIntake: null,
  intakesLoading: false,
  intakesError: null,
  intakeManagerFilters: {
    statusFilters: [],
  },

  // Actions
  setUploadStatus: status => set({ uploadStatus: status }),
  setUploadProgress: progress => set({ uploadProgress: progress }),
  setUploadError: error => set({ uploadError: error }),
  setIsExtracting: isExtracting => set({ isExtracting }),
  setIntakes: intakes => set({ intakes }),
  addIntake: intake =>
    set(state => ({
      intakes: [...state.intakes, intake],
    })),
  updateIntake: (id, updatedIntake) =>
    set(state => ({
      intakes: state.intakes.map(intake =>
        intake.id === id ? updatedIntake : intake
      ),
      selectedIntake:
        state.selectedIntake?.id === id ? updatedIntake : state.selectedIntake,
    })),
  setSelectedIntake: intake => set({ selectedIntake: intake }),
  setIntakesLoading: loading => set({ intakesLoading: loading }),
  setIntakesError: error => set({ intakesError: error }),
  resetUploadState: () =>
    set({
      uploadStatus: 'idle',
      uploadProgress: 0,
      uploadError: null,
      isExtracting: false,
      selectedIntake: null,
    }),
  setIntakeManagerFilters: filters => set({ intakeManagerFilters: filters }),
});

// Helper function to ensure date fields are always Date objects
export const ensureDateField = (
  dateValue: Date | string | undefined
): Date | undefined => {
  if (!dateValue) return undefined;
  if (dateValue instanceof Date) return dateValue;
  return new Date(dateValue);
};

// Helper function to get normalized IntakeRecord with Date objects
export const normalizeIntakeRecord = (
  intake: IntakeRecord
): IntakeRecord & {
  patient_dob: Date | undefined;
  assigned_date: Date | undefined;
  sla_deadline: Date | undefined;
  escalated_date: Date | undefined;
  reviewed_date: Date | undefined;
  decision_date: Date | undefined;
  authorization_request_date: Date | undefined;
  authorization_last_updated: Date | undefined;
  last_ai_analysis_date: Date | undefined;
  created_at: Date;
  updated_at: Date;
} => {
  return {
    ...intake,
    patient_dob: ensureDateField(intake.patient_dob),
    assigned_date: ensureDateField(intake.assigned_date),
    sla_deadline: ensureDateField(intake.sla_deadline),
    escalated_date: ensureDateField(intake.escalated_date),
    reviewed_date: ensureDateField(intake.reviewed_date),
    decision_date: ensureDateField(intake.decision_date),
    authorization_request_date: ensureDateField(
      intake.authorization_request_date
    ),
    authorization_last_updated: ensureDateField(
      intake.authorization_last_updated
    ),
    last_ai_analysis_date: ensureDateField(intake.last_ai_analysis_date),
    created_at: ensureDateField(intake.created_at)!,
    updated_at: ensureDateField(intake.updated_at)!,
  };
};
